import { baseApi } from "./baseApi";

export type ReviewStatus = "pending" | "approved" | "flagged";

export interface ReviewApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface ReviewRecord {
  id: number;
  rawId: string;
  customerName: string;
  city: string;
  projectId?: number | null;
  projectTitle: string;
  rating: number;
  comment: string;
  source: string;
  status: ReviewStatus;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface ReviewPayload {
  customerName: string;
  city: string;
  projectId?: number | null;
  projectTitle: string;
  rating: number;
  comment: string;
  source: string;
  status: ReviewStatus;
}

const toStatus = (value: unknown): ReviewStatus => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "approved" || normalized === "flagged") return normalized;
  return "pending";
};

const normalizeReview = (item: Record<string, any>): ReviewRecord => {
  const numericId = Number(item.id ?? 0);
  return {
    id: numericId,
    rawId: numericId ? `REV-${numericId}` : String(item.id ?? ""),
    customerName: String(item.customerName ?? ""),
    city: String(item.city ?? ""),
    projectId: item.projectId ?? null,
    projectTitle: String(item.projectTitle ?? ""),
    rating: Number(item.rating ?? 0),
    comment: String(item.comment ?? ""),
    source: String(item.source ?? "website"),
    status: toStatus(item.status),
    createdAt: item.createdAt ?? undefined,
    updatedAt: item.updatedAt ?? null,
  };
};

const getItems = (response: ReviewApiResponse<unknown> | unknown) => {
  const data = (response as ReviewApiResponse<unknown>)?.data ?? response;
  if (Array.isArray(data)) return data as Record<string, any>[];
  return [] as Record<string, any>[];
};

export const reviewsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchReviews: builder.query<ReviewRecord[], void>({
      query: () => ({ url: "/Review", method: "GET" }),
      transformResponse: (response: ReviewApiResponse<unknown> | unknown) =>
        getItems(response).map(normalizeReview),
      providesTags: ["Review"],
    }),
    createReview: builder.mutation<ReviewApiResponse, ReviewPayload>({
      query: (payload) => ({
        url: "/Review",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Review"],
    }),
    updateReviewStatus: builder.mutation<
      ReviewApiResponse,
      { id: number; status: ReviewStatus }
    >({
      query: ({ id, status }) => ({
        url: `/Review/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Review"],
    }),
    deleteReview: builder.mutation<ReviewApiResponse, number>({
      query: (id) => ({ url: `/Review/${id}`, method: "DELETE" }),
      invalidatesTags: ["Review"],
    }),
    fetchApprovedReviews: builder.query<ReviewRecord[], void>({
      query: () => ({ url: "/Review/approved", method: "GET" }),
      transformResponse: (response: ReviewApiResponse<unknown> | unknown) =>
        getItems(response).map(normalizeReview),
      providesTags: ["Review"],
    }),
  }),
});

export const {
  useFetchReviewsQuery,
  useCreateReviewMutation,
  useUpdateReviewStatusMutation,
  useDeleteReviewMutation,
  useFetchApprovedReviewsQuery,
} = reviewsApi;
